<template>
  <div class="contents">
    <div class="d-flex section-title-with-back" v-if="id">
      <img src="@/assets/images/Icon awesome-pen.png" alt width="22" height="22" />
      <p class="title-bk">生徒編集</p>
    </div>
    <div class="d-flex section-title-with-back" v-else>
      <img src="@/assets/images/Icon awesome-plus-circle.png" alt="" width="25" height="25" />
      <p class="title-bk">生徒登録</p>
    </div>
    <div class="d-flex section-subtitle-with-back">
      <router-link :to="{ name: id ? 'student-id-edit' : 'student-add', params: id ? { id } : {} }">
        戻る
      </router-link>
    </div>
    <div class="container">
      <table class="table">
        <tbody>
          <tr>
            <th scope="row" width="30%">以下の内容で登録しますか？</th>
            <td />
          </tr>
          <tr>
            <th scope="row">法人名（法人ID）</th>
            <td>{{ currentCorporationDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">ブランド名（ブランドID）</th>
            <td>{{ currentBrandDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">教場名（教場ID）</th>
            <td>{{ currentSchoolDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">コース名（コースID）</th>
            <td>{{ currentCourseDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">クラス名（クラスID）</th>
            <td>{{ currentTamaiClassDisplay }}</td>
          </tr>
          <tr>
            <th scope="row">生徒ID</th>
            <td>{{ currentStudent.uid }}</td>
          </tr>
          <tr>
            <th scope="row">生徒名</th>
            <td>{{ currentStudent.name }}</td>
          </tr>
          <tr>
            <th scope="row">生徒名（フリガナ）</th>
            <td>{{ currentStudent.nameKana }}</td>
          </tr>
          <tr>
            <th scope="row">生徒名（ローマ字）</th>
            <td>{{ currentStudent.nameRomaji }}</td>
          </tr>
          <tr>
            <th scope="row">生年月</th>
            <td>{{ currentStudent.yearOfBirth + '年' + currentStudent.monthOfBirth + '月' }}</td>
          </tr>
          <tr>
            <th scope="row">性別</th>
            <td>{{ currentStudentGender }}</td>
          </tr>
          <tr>
            <th scope="row">国籍</th>
            <td>{{ currentStudentCountryOfCitizenship }}</td>
          </tr>
          <tr>
            <th scope="row">居住国</th>
            <td>{{ currentStudentCountryOfResidence }}</td>
          </tr>
          <tr>
            <th scope="row">学年</th>
            <td>{{ currentStudentSchoolYear }}</td>
          </tr>
          <tr>
            <th scope="row">住所</th>
            <td>{{ currentStudent.address }}</td>
          </tr>
          <tr>
            <th scope="row">学習中の教材</th>
            <td>{{ currentStudentTeachingMaterial }}</td>
          </tr>
          <tr>
            <th scope="row">来月からの教材変更</th>
            <td>{{ currentStudentNextTeachingMaterial }}</td>
          </tr>
          <tr v-if="hasAdminPrivilege">
            <th scope="row">即日教材変更</th>
            <td>
              <span :class="{ 'text-red': currentStudent.isImmediately }">{{
                currentStudentImmediateNextTeachingMaterial
              }}</span>
            </td>
          </tr>
          <tr v-if="isStudentActive">
            <th scope="row">休会開始月</th>
            <td>{{ currentStudentDeactivateMonth }}</td>
          </tr>
        </tbody>
      </table>
      <div class="text-center">
        <button type="button" class="btn-confirm" @click="confirmRevised">修正</button>
        <button type="button" class="btn-red" @click="confirmSaved" v-if="id">変更</button>
        <button type="button" class="btn-red" @click="confirmSaved" :disabled="isLoading" v-else>
          登録
        </button>
      </div>
      <PopUpHolder
        ref="popup_holder"
        no-close-on-esc
        no-close-on-backdrop
        green-button-title="一覧に戻る"
        @greenClick="popupConfirmed"
      >
        <template v-slot:header v-if="id">生徒編集</template>
        <template v-slot:header v-else>生徒登録</template>
        <template v-slot:body v-if="id">生徒の登録情報の編集が完了しました！</template>
        <template v-slot:body v-else>生徒登録が完了しました</template>
      </PopUpHolder>
      <b-alert variant="danger" v-show="errorMessage" dismissible show>{{ errorMessage }}</b-alert>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import store from '@/store/index.js';
import PopUpHolder from '@/components/popups/PopUpHolder.vue';
import {
  basicCorporationNameIdPresent,
  basicBrandNameIdPresent,
  basicSchoolNameIdPresent,
  basicCourseNameIdPresent,
  basicTamaiClassNameIdPresent,
  basicJapaneseYearMonth,
  basicFieldPresent,
} from '@/presenters/fields';

export default {
  props: {
    id: {
      type: [String, Number],
      required: false,
    },
  },
  components: {
    PopUpHolder,
  },
  data() {
    return {
      errorMessage: null,
      isLoading: false,
      sortedAllMaterialCodes: ['600', '700', '400', '100', '200', '300', '500'],
    };
  },
  beforeRouteEnter(routeTo, routeFrom, next) {
    if (routeTo.params.id) {
      if (store.getters['student/existsUpdateRequiredParams']) {
        store.dispatch('student/fetchStudent', routeTo.params.id).then(() => {
          next();
        });
      } else {
        next({ name: 'student-id-edit', params: { id: routeTo.params.id } });
      }
    } else {
      if (store.getters['student/existsRegistrationRequiredParams']) {
        next();
      } else {
        next({ name: 'student-add' });
      }
    }
  },
  async mounted() {
    await this.fetchSchoolYears();
  },
  computed: {
    currentStudent() {
      if (this.editingStudent) {
        return this.editingStudent;
      } else {
        return this.student;
      }
    },
    currentCorporationDisplay() {
      return basicCorporationNameIdPresent(this.currentStudent.corporation);
    },
    currentBrandDisplay() {
      return basicBrandNameIdPresent(this.currentStudent.brand);
    },
    currentSchoolDisplay() {
      return basicSchoolNameIdPresent(this.currentStudent.school);
    },
    currentCourseDisplay() {
      return basicCourseNameIdPresent(this.currentStudent.course);
    },
    currentTamaiClassDisplay() {
      return basicTamaiClassNameIdPresent(this.currentStudent.tamaiClass);
    },
    currentStudentGender() {
      if (this.currentStudent.gender) {
        return basicFieldPresent(this.getGenderById(this.currentStudent.gender.id), 'name');
      }
      return basicFieldPresent(this.getGenderById(this.currentStudent.genderId), 'name');
    },
    currentStudentCountryOfCitizenship() {
      if (this.currentStudent.citizenship) {
        return basicFieldPresent(this.getCountryById(this.currentStudent.citizenship.id), 'companyjp');
      }
      return basicFieldPresent(this.getCountryById(this.currentStudent.countryOfCitizenshipId), 'companyjp');
    },
    currentStudentCountryOfResidence() {
      if (this.currentStudent.residence) {
        return basicFieldPresent(this.getCountryById(this.currentStudent.residence.id), 'companyjp');
      }
      return basicFieldPresent(this.getCountryById(this.currentStudent.countryOfResidenceId), 'companyjp');
    },
    currentStudentSchoolYear() {
      if (this.currentStudent.schoolYear) {
        return basicFieldPresent(this.getSchoolYearById(this.currentStudent.schoolYear.id), 'name');
      }
      return basicFieldPresent(this.getSchoolYearById(this.currentStudent.schoolYearId), 'name');
    },
    currentStudentTeachingMaterial() {
      return this.getTeachingMaterialNamesByCodes(this.currentStudent.figuredTeachingMaterialCodes);
    },
    currentStudentNextTeachingMaterial() {
      if (!this.currentStudent.nextFiguredTeachingMaterialCodes) {
        return 'ー';
      } else if (!this.currentStudent.nextFiguredTeachingMaterialCodes[0]) {
        return 'なし（来月からの教材変更を設定している場合は全てキャンセルされます）';
      }
      const sortedNextFiguredTeachingMaterialCodes = this.sortedAllMaterialCodes.filter(
        (i) => this.currentStudent.nextFiguredTeachingMaterialCodes.indexOf(i) != -1
      );
      let text = this.getTeachingMaterialNamesByCodes(sortedNextFiguredTeachingMaterialCodes);
      if (this.currentStudent.prevNextFiguredTeachingMaterialCodes) {
        const sortedPrevNextFiguredTeachingMaterialCodes = this.sortedAllMaterialCodes.filter(
          (i) => this.currentStudent.prevNextFiguredTeachingMaterialCodes.indexOf(i) != -1
        );
        if (
          sortedNextFiguredTeachingMaterialCodes.toString() == sortedPrevNextFiguredTeachingMaterialCodes.toString()
        ) {
          text = '変更なし（既に設定済みの場合、予約教材に変更を加えません）';
        }
      }
      return text;
    },
    currentStudentImmediateNextTeachingMaterial() {
      if (this.currentStudent.isImmediately) {
        return '即日変更する（来月からの教材変更を設定している場合は全てキャンセルされます）';
      } else {
        return 'ー';
      }
    },
    currentStudentDeactivateMonth() {
      if (this.currentStudent.deactivateMonth) {
        return this.currentStudent.deactivateMonth.text;
      }
      return basicJapaneseYearMonth(this.currentStudent.deactivatedDatetime);
    },
    ...mapState({
      student: (state) => state.student.student,
      editingStudent: (state) => state.student.editingStudent,
    }),
    ...mapGetters('student', ['isStudentActive']),
    ...mapGetters('country', ['getCountryById']),
    ...mapGetters('gender', ['getGenderById']),
    ...mapGetters('school_year', ['getSchoolYearById']),
    ...mapGetters('teaching_material', ['getTeachingMaterialNamesByCodes']),
    ...mapGetters('role', ['hasAdminPrivilege']),
  },
  methods: {
    confirmRevised() {
      if (this.id) {
        // This path is for Editing existing Student flow
        this.$logger.debug('Need revise, go back to ID ' + this.id);
        this.$router.push({ name: 'student-id-edit', params: { id: this.id } });
      } else {
        // This path is for Adding new Student flow
        this.$logger.debug('Need revise, go back');
        this.$router.push({ name: 'student-add' });
      }
    },
    confirmSaved() {
      if (this.id) {
        this.$logger.debug('Finished Editing: ' + JSON.stringify(this.currentStudent));
        // This path is for Editing existing Student flow
        this.finishEditingStudent(this.id).then(
          (response) => {
            this.$logger.debug('Show success popup ' + response);
            this.$refs['popup_holder'].show();
          },
          (error) => {
            this.errorMessage = error;
            this.$logger.debug('Error ' + error);
          }
        );
      } else {
        this.isLoading = true;
        this.$logger.debug('Finished Adding: ' + JSON.stringify(this.currentStudent));
        // This path is for Adding new Student flow
        this.finishEditingStudent().then(
          (response) => {
            this.$logger.debug('Show success popup ' + response);
            this.$refs['popup_holder'].show();
          },
          (error) => {
            this.isLoading = false;
            this.errorMessage = error;
            this.$logger.debug('Error ' + error);
          }
        );
      }
    },
    popupConfirmed() {
      this.$logger.debug('Popup done');
      this.$router.push({ name: 'student' });
    },
    ...mapActions('student', ['finishEditingStudent']),
    ...mapActions('school_year', ['fetchSchoolYears']),
  },
};
</script>

<style scoped>
.btn-confirm {
  margin-left: 0px;
}
.text-red {
  color: red;
}
</style>
